import React, { useState } from 'react';
import {
  Box,
  Stack,
  Container,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import Iconify from 'src/components/Iconify';
import moment from 'moment';
import { NumericFormat } from 'react-number-format';

const cardData = [
  {
    label: 'Total Balance',
    balance: '1000000',
    icon: (
      <Iconify
        icon={'material-symbols-light:account-balance-wallet'}
        sx={{
          color: 'rgba(1,15,88,0.8)',
          fontSize: 50,
          position: 'absolute',
          right: 10,
          bottom: 10
        }}
      />
    ),
    gradient: 'linear-gradient(135deg, #010F58,#8fd3f4)',
    decoration: (
      <Box
        sx={{
          position: 'absolute',
          width: '120%',
          height: '90%',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.18) 100%, transparent 100%)',
          borderRadius: '50%',
          top: '4%',
          left: '-7%',
          transform: 'rotate(155deg)'
        }}
      />
    )
  },
  {
    label: 'Requested Amount',
    balance: '1000000',
    icon: (
      <Iconify
        icon={'fa6-solid:hand-holding-dollar'}
        sx={{
          color: 'rgba(1,15,88,0.95)',
          fontSize: 40,
          position: 'absolute',
          right: 10,
          bottom: 18
        }}
      />
    ),
    gradient: 'linear-gradient(135deg, #010F58,#8fd3f4)',
    decoration: (
      <Box
        sx={{
          position: 'absolute',
          width: '150%',
          height: '145%',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.18) 100%, transparent 100%)',
          borderRadius: '50%',
          bottom: '-118%',
          right: '-22%',
          transform: 'rotate(-45deg)'
        }}
      />
    )
  },
  {
    label: 'Available Balance',
    balance: '1000000',
    icon: (
      <Iconify
        icon={'ic:baseline-account-balance'}
        sx={{
          color: 'rgba(1,15,88,0.95)',
          fontSize: 40,
          position: 'absolute',
          right: 10,
          bottom: 15
        }}
      />
    ),
    gradient: 'linear-gradient(135deg, #010F58, #8fd3f4)',
    decoration: (
      <Box
        sx={{
          position: 'absolute',
          width: '140%',
          height: '140%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 40%, transparent 90%)',
          borderRadius: '50%',
          top: '20%',
          left: '-10%',
          transform: 'rotate(15deg)'
        }}
      />
    )
  }
];

export default function Wallet() {
  const [openModal, setOpenModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([
    {
      amount: 100,
      request_date: '2024-01-28',
      approval_date: '2024-01-28',
      status: 'Approved',
      remarks: 'Processed'
    },
    {
      amount: 50,
      request_date: '2024-01-27',
      approval_date: '2024-01-28',
      status: 'Declined',
      remarks: 'Insufficient balance'
    }
  ]);

  return (
    <Container>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" sx={{ color: '#010f58' }}>
          Wallet
        </Typography>
        <Button
          variant="contained"
          startIcon={<Iconify icon="ph:hand-withdraw-fill" style={{ fontSize: 15 }} />}
          sx={{ alignSelf: 'end', fontSize: 11 }}
          onClick={() => setOpenModal(true)}
        >
          Withdraw Request
        </Button>
      </Stack>

      {/* Balance Summary */}
      <Stack direction="row" spacing={2} mb={3}>
        {cardData.map((card, index) => (
          <Card
            key={index}
            sx={{
              flex: 1,
              p: 1.5,
              textAlign: 'center',
              borderRadius: 2,
              boxShadow: 5,
              background: card.gradient,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              {card.icon}
              {card.decoration}
              <Typography
                variant="h6"
                color="primary"
                sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#ffffff' }}
              >
                {card.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                <NumericFormat
                  value={card.balance ? parseFloat(card.balance).toFixed(2) : '0.00'}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                />
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Withdrawal Requests Table */}
      <TableContainer
        component={Paper}
        sx={{ boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)', mt: 2, mb: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '12px', color: '#010f58' }}>Requested Amount</TableCell>
              <TableCell sx={{ fontSize: '12px', color: '#010f58' }}>Requested Date</TableCell>
              <TableCell sx={{ fontSize: '12px', color: '#010f58' }}>Remarks</TableCell>
              <TableCell sx={{ fontSize: '12px', color: '#010f58' }}>Approval Date</TableCell>
              <TableCell sx={{ fontSize: '12px', color: '#010f58' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((req, index) => (
              <TableRow key={index}>
                <TableCell align="left" style={{ fontSize: 12 }}>
                  <NumericFormat
                    value={req.amount ? parseFloat(req.amount).toFixed(2) : '0.00'}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={'$'}
                  />
                </TableCell>
                <TableCell align="left" style={{ fontSize: 12 }}>
                  {/* {req.date} */}
                  {moment(req.request_date).format('MM/DD/YYYY')}
                </TableCell>
                <TableCell align="left" style={{ fontSize: 12 }}>
                  {req.remarks}
                </TableCell>
                <TableCell align="left" style={{ fontSize: 12 }}>
                  {moment(req.date).format('MM/DD/YYYY')}
                </TableCell>
                <TableCell align="left" style={{ fontSize: 12 }} sx={{ width: '130px' }}>
                  <Typography
                    className={
                      (req?.status.toLowerCase() == 'approved' && 'status-green') ||
                      (req?.status.toLowerCase() == 'pending' && 'status-blue') ||
                      (req?.status.toLowerCase() == 'declined' && 'status-red')
                    }
                  >
                    {req?.status || 'N/A'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Withdraw Request Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
        <DialogTitle>Withdraw Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <LoadingButton onClick={() => {}} loading={loading} variant="contained">
            Request
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
